import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { staffProfileAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { useAuth } from "../AuthContext";
import {
  EMPLOYMENT_TYPES,
  formatCentsAsCurrency,
  getEmploymentTypeLabel,
  isAdminRole,
  toDateInputValue,
} from "../../utils/workforceUtils";
import StaffLayout from "./StaffLayout";
import "./StaffProfileList.css";

/**
 * Workforce — Staff list (level 1 of TIME-001 spec).
 * Admin-only screen. Displays all StaffProfile records with filters and a
 * primary CTA to create a new staff profile. Row click routes to the detail
 * form. Compensation column (hourlyRate) is hidden for non-admin viewers per
 * data-models §17.1.1.
 */
const StaffProfileList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "active"
  );
  const [typeFilter, setTypeFilter] = useState(
    searchParams.get("employmentType") || ""
  );

  const adminAccess = isAdminRole(user?.role);

  const fetchStaffProfiles = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.employmentType = typeFilter;
      const response = await staffProfileAPI.list(params);
      // Backend returns { profiles, total, limit, skip }; legacy shapes
      // (`staffProfiles`, `data`, bare array) kept for graceful degradation.
      const list =
        response.data?.profiles ||
        response.data?.staffProfiles ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);
      setStaffProfiles(list);
      setError("");
    } catch (err) {
      handleApiError(
        err,
        "StaffProfileList.fetch",
        setError,
        "Error loading staff profiles. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    if (!adminAccess) {
      setLoading(false);
      return;
    }
    fetchStaffProfiles();
  }, [adminAccess, fetchStaffProfiles]);

  // Sync filter state to query params for shareable / refreshable URLs.
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTerm) next.set("search", searchTerm);
    if (statusFilter && statusFilter !== "active") next.set("status", statusFilter);
    if (typeFilter) next.set("employmentType", typeFilter);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [searchTerm, statusFilter, typeFilter, searchParams, setSearchParams]);

  const filteredProfiles = useMemo(() => {
    if (!searchTerm) return staffProfiles;
    const needle = searchTerm.toLowerCase();
    return staffProfiles.filter((sp) => {
      // userId is populated to a User object on the list response; include
      // its identity fields in the haystack so name/email search works.
      const linkedUser =
        sp.userId && typeof sp.userId === "object" ? sp.userId : null;
      const haystack = [
        linkedUser?.firstName,
        linkedUser?.lastName,
        linkedUser?.email,
        sp.firstName,
        sp.lastName,
        sp.email,
        sp.user?.firstName,
        sp.user?.lastName,
        sp.user?.email,
        sp.primaryRole,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [staffProfiles, searchTerm]);

  const handleRowClick = (id) => {
    navigate(`/staff/staff-profiles/${id}`);
  };

  if (!adminAccess) {
    return (
      <StaffLayout>
        <div className="staffprofile-list-container">
          <h1>Workforce — Staff Profiles</h1>
          <p className="staffprofile-rbac-warning" role="alert">
            You do not have permission to view staff profiles. Contact an
            administrator if you need access.
          </p>
        </div>
      </StaffLayout>
    );
  }

  return (
    <StaffLayout>
      <div className="staffprofile-list-container">
        <header className="staffprofile-list-header">
          <div>
            <h1>Workforce — Staff Profiles</h1>
            <p className="staffprofile-list-subtitle">
              {staffProfiles.length} record{staffProfiles.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="staffprofile-list-header-actions">
            <Link to="/staff/job-codes" className="staffprofile-btn staffprofile-btn-secondary">
              Manage Job Codes
            </Link>
            <Link
              to="/staff/staff-profiles/new"
              className="staffprofile-btn staffprofile-btn-primary"
            >
              + Add Staff
            </Link>
          </div>
        </header>

        {error && (
          <div className="staffprofile-error" role="alert">
            {error}
          </div>
        )}

        <div className="staffprofile-filters">
          <input
            type="search"
            className="staffprofile-search"
            placeholder="Search by name, email, or role"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search staff profiles"
          />
          <label className="staffprofile-filter-label">
            <span className="staffprofile-filter-label-text">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="">All</option>
            </select>
          </label>
          <label className="staffprofile-filter-label">
            <span className="staffprofile-filter-label-text">Type</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="Filter by employment type"
            >
              <option value="">All</option>
              {EMPLOYMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <p className="staffprofile-loading">Loading staff profiles...</p>
        ) : filteredProfiles.length === 0 ? (
          <p className="staffprofile-empty">
            No staff profiles match the current filters.
          </p>
        ) : (
          <div className="staffprofile-table-wrap">
            <table className="staffprofile-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col">Type</th>
                  <th scope="col">Start</th>
                  <th scope="col">Status</th>
                  {/* Hourly rate is admin-only; this scope already gates the
                      whole page on adminAccess so the column always renders. */}
                  <th scope="col">Hourly rate</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.map((sp) => {
                  const id = sp._id || sp.id;
                  // Backend populates userId with a User sub-doc; unwrap it
                  // before falling back to flat / legacy `user` shape.
                  const linkedUser =
                    sp.userId && typeof sp.userId === "object" ? sp.userId : null;
                  const firstName =
                    linkedUser?.firstName || sp.firstName || sp.user?.firstName || "";
                  const lastName =
                    linkedUser?.lastName || sp.lastName || sp.user?.lastName || "";
                  const fullName =
                    `${firstName} ${lastName}`.trim() ||
                    linkedUser?.email ||
                    sp.email ||
                    sp.user?.email ||
                    "(no name)";
                  const status = sp.active === false ? "inactive" : "active";
                  return (
                    <tr
                      key={id}
                      className="staffprofile-row"
                      onClick={() => handleRowClick(id)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleRowClick(id);
                        }
                      }}
                    >
                      <td>{fullName}</td>
                      <td>{sp.primaryRole || "—"}</td>
                      <td>
                        <span
                          className={`staffprofile-type-chip staffprofile-type-${sp.employmentType || "unknown"}`}
                        >
                          {getEmploymentTypeLabel(sp.employmentType)}
                        </span>
                      </td>
                      <td>{toDateInputValue(sp.startDate) || "—"}</td>
                      <td>
                        <span
                          className={`staffprofile-status-pill staffprofile-status-${status}`}
                        >
                          {status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="staffprofile-rate-cell">
                        {formatCentsAsCurrency(sp.hourlyRate) || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </StaffLayout>
  );
};

export default StaffProfileList;
